// One waiver claim, as shown in the Lineup tab's Waiver claims sheet and in
// the bid sheet's list of other claims.
//
// The second line says what the claim does to the roster. A claim with no drop
// team is not missing one: it fills an empty roster slot (migration 041), and
// the old "$31 · drop" with a blank name read as a bug.
import React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import TeamLogoImage from "../league/TeamLogoImage";

const SKIP_REASONS = {
  over_budget: "Skipped: over your budget",
  target_taken: "Skipped: team already taken",
  drop_team_gone: "Skipped: drop team no longer on your roster",
};

const STATUS_STYLES = {
  won: { label: "Won", color: "var(--color-success-text)", bg: "var(--color-success-bg)" },
  lost: { label: "Lost", color: "var(--color-danger-text)", bg: "var(--color-danger-bg)" },
  skipped: { label: "Skipped", color: "var(--color-text-muted)", bg: "var(--color-bg-subtle)" },
};

export default function ClaimRow({
  bid, teams, rank = null, onCancel = null, noRoomIfAllWin = false,
  reorderable = false, onMoveUp = null, onMoveDown = null,
}) {
  const team = teams?.[bid.team_id];
  const school = team?.school || bid.team_id;
  const drop = bid.drop_team_id ? teams?.[bid.drop_team_id] : null;
  const dropSchool = drop?.school || bid.drop_team_id;
  const status = STATUS_STYLES[bid.status];

  // resolve_waivers records a no-drop claim that found no empty slot as
  // drop_team_gone, the same as a swap whose drop team had already left. The
  // claim itself says which it was.
  const skipReason = bid.status === "skipped" && bid.skip_reason === "drop_team_gone" && !bid.drop_team_id
    ? "Skipped: no empty roster slot left"
    : bid.status === "skipped" ? SKIP_REASONS[bid.skip_reason] : null;
  const detail = skipReason || null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid var(--color-bg-subtle)" }}>
      {reorderable ? (
        <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          <span style={{ width: 14, textAlign: "center", fontSize: 12, fontWeight: 700, color: "var(--color-text-faint)" }}>
            {rank}
          </span>
          <div style={{ display: "flex", flexDirection: "column" }}>
          <button
            onClick={onMoveUp}
            disabled={!onMoveUp}
            aria-label={`Move ${school} up`}
            style={{
              border: "none", background: "none", padding: 2, lineHeight: 0,
              cursor: onMoveUp ? "pointer" : "default", opacity: onMoveUp ? 1 : 0.25,
            }}
          >
            <ChevronUp size={16} color="var(--color-text-muted)" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={!onMoveDown}
            aria-label={`Move ${school} down`}
            style={{
              border: "none", background: "none", padding: 2, lineHeight: 0,
              cursor: onMoveDown ? "pointer" : "default", opacity: onMoveDown ? 1 : 0.25,
            }}
          >
            <ChevronDown size={16} color="var(--color-text-muted)" />
          </button>
          </div>
        </div>
      ) : rank != null ? (
        <span style={{ width: 16, flexShrink: 0, textAlign: "center", fontSize: 12, fontWeight: 700, color: "var(--color-text-faint)" }}>
          {rank}
        </span>
      ) : null}
      <TeamLogoImage teamName={school} primaryColor={team?.colors?.primary} size={36} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-strong)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {school}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, color: "var(--color-text-muted)", minWidth: 0 }}>
          {detail ? (
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{detail}</span>
          ) : bid.drop_team_id ? (
            <>
              <span style={{ flexShrink: 0 }}>Drops</span>
              <TeamLogoImage teamName={dropSchool} primaryColor={drop?.colors?.primary} size={16} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{dropSchool}</span>
            </>
          ) : noRoomIfAllWin ? (
            <span style={{ color: "var(--color-captain-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>No empty slot left if the others win</span>
          ) : (
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Fills an empty roster slot</span>
          )}
        </div>
      </div>

      <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: "var(--color-brand)", fontVariantNumeric: "tabular-nums" }}>
          ${bid.amount}
        </span>
        {onCancel ? (
          <button
            onClick={() => onCancel(bid.id)}
            aria-label={`Cancel claim on ${school}`}
            style={{ fontSize: 13, fontWeight: 600, color: "var(--color-danger-text)", border: "none", background: "none", cursor: "pointer", padding: "4px 0 4px 8px", minHeight: 32 }}
          >
            Cancel
          </button>
        ) : status ? (
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: status.color, backgroundColor: status.bg, borderRadius: 6, padding: "2px 6px" }}>
            {status.label}
          </span>
        ) : null}
      </div>
    </div>
  );
}

// Resolution order, as resolve_waivers processes bids: by claim_rank tier
// (1 first), and within a tie, highest amount then oldest. A bid with no
// claim_rank (the draft bid a BidSheet is composing, not yet saved) sorts
// last, matching where place_bid would put it: new claims join the bottom
// of the list.
export function sortByProcessingOrder(bids) {
  return [...bids].sort((a, b) =>
    (a.claim_rank ?? Infinity) - (b.claim_rank ?? Infinity)
    || b.amount - a.amount
    || new Date(a.created_at) - new Date(b.created_at)
  );
}

export const ROSTER_SIZE = 7;

// The claims with no drop team that would find no empty slot if every claim
// ahead of them won. resolve_waivers fills empty slots in processing order and
// skips a no-drop claim once they are gone, so with two empty slots and three
// no-drop claims the lowest one is skipped. A claim with a drop team swaps and
// never needs a slot.
//
// `claims` must already be in processing order.
export function claimsWithoutRoom(claims, emptySlots) {
  const ids = new Set();
  let slots = Math.max(0, emptySlots);
  for (const claim of claims) {
    if (claim.drop_team_id) continue;
    if (slots > 0) slots -= 1;
    else ids.add(claim.id);
  }
  return ids;
}
